// Developers.jsx
import React, { useEffect, useState } from 'react';
import { sendRequest } from '../../utils/dateHelpers';
import CSVUploader from './CSVUploader';
import CSVUploaderGrouping from './CSVUploaderGrouping';
import CustomGroupingEmail from './CustomGroupingEmail';
import DeveloperTable from './Developers/DeveloperTable';
import { toast } from 'react-toastify';

const MERGE_SUGGESTIONS_URL = '/developers/merge-suggestions';
const AI_AGENT_TAG_KEY = 'ai agent';
const isAiAgentTag = tag =>
  String(tag?.label || tag?.tagName || tag || '')
    .trim()
    .toLowerCase() === AI_AGENT_TAG_KEY;

const getSuggestionTitle = suggestion => {
  const count = suggestion.authors.length;
  return count === 1
    ? 'This record may belong to the same developer'
    : `These ${count} records may belong to the same developer`;
};

const Developers = () => {
  const [data, setData] = useState([]);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updateData, setUpdateData] = useState(null);
  const [tagData, setTagData] = useState({});
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [mergeSuggestions, setMergeSuggestions] = useState([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(
    () => document?.body?.classList.contains('dark') || false
  );

  useEffect(() => {
    const syncDarkMode = () => {
      setIsDarkMode(document.body.classList.contains('dark'));
    };

    syncDarkMode();

    const observer = new MutationObserver(syncDarkMode);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const responseTags = await sendRequest('/tags/get', 'GET');
      const tagMap = responseTags?.data?.reduce((acc, item) => {
        acc[item._id] = item.tagName;
        return acc;
      }, {});
      setTagData(tagMap);

      const responseData = await sendRequest('/developers/get', 'GET');
      const developers = responseData?.data || [];
      setData(developers);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMergeSuggestions = async () => {
    try {
      setSuggestionsLoading(true);
      const response = await sendRequest(MERGE_SUGGESTIONS_URL, 'GET');
      setMergeSuggestions(response?.data || []);
    } catch (error) {
      console.error('Error fetching merge suggestions:', error);
      setMergeSuggestions([]);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchMergeSuggestions();
  }, []);

  const handleEdit = developer => {
    if (
      updateData &&
      updateData._id !== developer._id &&
      !window.confirm('Ignore unsaved changes and edit this developer instead?')
    ) {
      return false;
    }
    setFormData({
      name: developer.author,
      emails: (developer.author_email || []).map(value =>
        String(value).trim().toLowerCase()
      ),
      tags: (developer.tags || []).map(value => ({
        value,
        label: tagData[value]
      }))
    });
    setUpdateData(developer);
    return true;
  };

  const clearProgress = () => {
    setFormData({});
    setUpdateData(null);
  };

  const handleSave = async formPayload => {
    if (!updateData) return;
    const previouslyAssignedAiAgent = (updateData.tags || []).some(tagId =>
      isAiAgentTag(tagData[tagId])
    );
    const isAddingAiAgent =
      !previouslyAssignedAiAgent && (formPayload.tags || []).some(isAiAgentTag);
    if (
      isAddingAiAgent &&
      !window.confirm(
        'Accounts marked as AI Agents are assigned 100% AI Leverage directly. Do you confirm that this account is an autonomous AI agent that operates independently?'
      )
    ) {
      return;
    }
    try {
      setSaving(true);
      const payload = {
        uid: updateData._id,
        name: String(formPayload.name || '').trim(),
        email: formPayload.emails || [],
        tags: (formPayload.tags || [])
          .filter(item => !item.isNew)
          .map(item => item.value),
        newTagNames: (formPayload.tags || [])
          .filter(item => item.isNew)
          .map(item => item.label),
        ...(isAddingAiAgent && { confirmAiAgentAssignment: true })
      };
      let response;
      try {
        response = await sendRequest('/developers/edit', 'POST', payload);
      } catch (error) {
        if (error?.errorData?.code !== 'developer_email_conflict') throw error;
        const conflicts = error.errorData.conflicts || [];
        const summary = conflicts
          .map(item => `${item.email} (${item.developerName})`)
          .join(', ');
        if (
          !window.confirm(`Move these email(s) to this developer? ${summary}`)
        ) {
          return;
        }
        response = await sendRequest('/developers/edit', 'POST', {
          ...payload,
          confirmEmailTransfer: true
        });
      }

      if (response.success) {
        toast.success('Developer updated.');
        clearProgress();
        await fetchData();
        await fetchMergeSuggestions();
      }
    } catch (error) {
      console.error('Error saving data:', error);
      toast.error(
        error?.errorData?.message ||
          error?.message ||
          'Developer could not be updated.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleMerge = async () => {
    try {
      setLoading(true);
      const response = await sendRequest('/developers/batch-merge', 'POST', {
        developers: [...selectedRows]
      });
      setSelectedRows(new Set());
      if (response.success) {
        clearProgress();
        await fetchData();
        await fetchMergeSuggestions();
      }
    } catch (error) {
      console.error('Error saving data:', error);
      setLoading(false);
    }
  };

  const handleSuggestionAction = async (suggestionId, action) => {
    try {
      setSuggestionsLoading(true);
      const response = await sendRequest(
        `${MERGE_SUGGESTIONS_URL}/${suggestionId}/${action}`,
        'POST'
      );

      if (response.success) {
        toast.success(
          action === 'apply'
            ? 'Developer records merged.'
            : 'Merge suggestion dismissed.'
        );
        await fetchData();
        await fetchMergeSuggestions();
      }
    } catch (error) {
      console.error(`Error updating merge suggestion:`, error);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const handleApplyAllMergeSuggestions = async () => {
    if (mergeSuggestions.length === 0) return;

    try {
      setSuggestionsLoading(true);

      for (const suggestion of mergeSuggestions) {
        await sendRequest(
          `${MERGE_SUGGESTIONS_URL}/${suggestion.id}/apply`,
          'POST'
        );
      }

      toast.success('All developer merge suggestions applied.');
      await fetchData();
      await fetchMergeSuggestions();
    } catch (error) {
      console.error('Error applying all merge suggestions:', error);
      toast.error(error.message || 'Applying all merge suggestions failed.');
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const handleUnmerge = async developer => {
    const mergeHistory = Array.isArray(developer.merge_history)
      ? developer.merge_history
      : [];
    const lastMerge = mergeHistory[mergeHistory.length - 1];
    const restoredAuthors = lastMerge?.merged_authors || [];
    const restoredNames = restoredAuthors
      .map(author => author.author || author.uid || author._id)
      .filter(Boolean)
      .join(', ');

    const confirmed = window.confirm(
      `Undo the latest merge for ${developer.author || 'this developer'}? This will split it back into ${restoredAuthors.length + 1} records${restoredNames ? ` including ${restoredNames}` : ''}.`
    );
    if (!confirmed) return;

    try {
      setLoading(true);
      const response = await sendRequest(
        `/developers/${developer.uid || developer._id}/unmerge`,
        'POST'
      );
      if (response.success) {
        toast.success('Latest developer merge was undone.');
        clearProgress();
        await fetchData();
        await fetchMergeSuggestions();
      }
    } catch (error) {
      console.error('Error undoing developer merge:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto grid grid-cols-1 gap-6">
      {mergeSuggestions.length > 0 && (
        <section
          className={`rounded-[24px] border px-6 py-5 shadow-[0_16px_45px_rgba(15,23,42,0.08)] ${
            isDarkMode
              ? 'border-amber-700/40 bg-amber-950/20'
              : 'border-amber-200 bg-amber-50'
          }`}
        >
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p
                className={`text-sm font-semibold ${
                  isDarkMode ? 'text-amber-200' : 'text-amber-800'
                }`}
              >
                {mergeSuggestions.length} developer merge suggestion
                {mergeSuggestions.length === 1 ? '' : 's'} pending
              </p>
              <p
                className={`mt-1 text-sm ${
                  isDarkMode ? 'text-amber-100/80' : 'text-amber-700'
                }`}
              >
                Merge duplicates before they skew your reports and filters.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {suggestionsOpen && (
                <button
                  type="button"
                  disabled={suggestionsLoading || mergeSuggestions.length === 0}
                  onClick={handleApplyAllMergeSuggestions}
                  className="inline-flex items-center justify-center rounded-2xl bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Apply All
                </button>
              )}
              <button
                type="button"
                onClick={() => setSuggestionsOpen(value => !value)}
                className={`inline-flex items-center justify-center rounded-2xl px-4 py-2.5 text-sm font-semibold transition ${
                  isDarkMode
                    ? 'bg-amber-300 text-slate-950 hover:bg-amber-200'
                    : 'bg-slate-900 text-white hover:bg-slate-800'
                }`}
              >
                {suggestionsOpen ? 'Hide suggestions' : 'Review suggestions'}
              </button>
            </div>
          </div>

          {suggestionsOpen && (
            <div className="mt-5 grid gap-4">
              {mergeSuggestions.map(suggestion => (
                <div
                  key={suggestion.id}
                  className={`rounded-[18px] border p-4 ${
                    isDarkMode
                      ? 'border-amber-700/40 bg-slate-950/30'
                      : 'border-amber-200 bg-white'
                  }`}
                >
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between lg:gap-4">
                    <div className="min-w-0 flex-1">
                      <p
                        className={`font-semibold ${
                          isDarkMode ? 'text-white' : 'text-slate-900'
                        }`}
                      >
                        {getSuggestionTitle(suggestion)}
                      </p>
                      <p
                        className={`mt-1 text-sm ${
                          isDarkMode ? 'text-slate-300' : 'text-slate-600'
                        }`}
                      >
                        {suggestion.reason}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {suggestion.authors.map(author => (
                          <span
                            key={`name-${suggestion.id}-${author._id}`}
                            className={`rounded-full px-3 py-1 text-xs font-semibold ${
                              isDarkMode
                                ? 'bg-slate-800 text-slate-200'
                                : 'bg-slate-100 text-slate-700'
                            }`}
                          >
                            {author.author || 'Unknown Developer'}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-nowrap gap-2 self-start">
                      <button
                        type="button"
                        disabled={suggestionsLoading}
                        onClick={() =>
                          handleSuggestionAction(suggestion.id, 'apply')
                        }
                        className="inline-flex items-center justify-center rounded-2xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-400 disabled:opacity-60"
                      >
                        Apply
                      </button>
                      <button
                        type="button"
                        disabled={suggestionsLoading}
                        onClick={() =>
                          handleSuggestionAction(suggestion.id, 'cancel')
                        }
                        className={`inline-flex items-center justify-center rounded-2xl border px-4 py-2 text-sm font-semibold transition disabled:opacity-60 ${
                          isDarkMode
                            ? 'border-red-500/40 text-red-200 hover:bg-red-500/10'
                            : 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
                        }`}
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                  <div className="mt-4 grid gap-3">
                    {suggestion.authors.map(author => (
                      <div
                        key={author._id}
                        className={`rounded-2xl border px-4 py-3 ${
                          isDarkMode
                            ? 'border-slate-700 bg-boxdark'
                            : 'border-slate-200 bg-slate-50'
                        }`}
                      >
                        <p
                          className={`text-sm font-semibold ${
                            isDarkMode ? 'text-white' : 'text-slate-900'
                          }`}
                        >
                          {author.author || 'Unknown Developer'}
                        </p>
                        <p
                          className={`mt-1 break-all text-xs ${
                            isDarkMode ? 'text-slate-400' : 'text-slate-500'
                          }`}
                        >
                          {(author.author_email || []).join(', ') || 'No email'}
                        </p>
                        {(author.provider_id || []).length > 0 && (
                          <p
                            className={`mt-1 break-all text-xs ${
                              isDarkMode ? 'text-slate-500' : 'text-slate-400'
                            }`}
                          >
                            Provider IDs: {author.provider_id.join(', ')}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <section
        className={`rounded-[28px] border shadow-[0_20px_60px_rgba(15,23,42,0.08)] ${
          isDarkMode
            ? 'border-strokedark bg-boxdark'
            : 'border-slate-200 bg-white'
        }`}
      >
        <div
          className={`flex flex-col gap-5 border-b px-6 py-6 sm:px-8 lg:flex-row lg:items-start lg:justify-between ${
            isDarkMode ? 'border-strokedark' : 'border-slate-200'
          }`}
        >
          <div className="max-w-2xl">
            <h2
              className={`text-2xl font-semibold tracking-tight ${
                isDarkMode ? 'text-white' : 'text-slate-900'
              }`}
            >
              Developer records
            </h2>
            <p
              className={`mt-2 text-sm leading-6 ${
                isDarkMode ? 'text-slate-400' : 'text-slate-500'
              }`}
            >
              Browse current mappings, refine developer names, and edit tags
              without disrupting the rest of the setup.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <CSVUploader
              triggerGetData={fetchData}
              buttonClassName={`inline-flex items-center justify-center rounded-2xl border px-4 py-2.5 text-sm font-semibold transition ${
                isDarkMode
                  ? 'border-slate-700 bg-white text-slate-900 hover:bg-slate-100'
                  : 'border-slate-200 bg-slate-900 text-white hover:bg-slate-800'
              }`}
              buttonLabel="Upload Tags CSV"
            />
            <CSVUploaderGrouping
              triggerGetData={fetchData}
              buttonClassName={`inline-flex items-center justify-center rounded-2xl border px-4 py-2.5 text-sm font-semibold transition ${
                isDarkMode
                  ? 'border-slate-700 bg-boxdark-2 text-slate-200 hover:border-slate-600 hover:bg-slate-800'
                  : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
              }`}
              buttonLabel="Import Grouping CSV"
            />
            <CustomGroupingEmail
              triggerGetData={fetchData}
              data={data}
              tagData={tagData}
              buttonClassName={`inline-flex items-center justify-center rounded-2xl border px-4 py-2.5 text-sm font-semibold transition ${
                isDarkMode
                  ? 'border-blue-800/70 bg-blue-950/40 text-blue-300 hover:bg-blue-900/50'
                  : 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
              }`}
              buttonLabel="Custom Grouping"
            />
          </div>
        </div>

        <div className="px-4 py-5 sm:px-6">
          <DeveloperTable
            data={data}
            tagData={tagData}
            loading={loading}
            saving={saving}
            onEdit={handleEdit}
            onMerge={handleMerge}
            onUnmerge={handleUnmerge}
            editingDeveloperId={updateData?._id || null}
            formData={formData}
            setFormData={setFormData}
            onSave={handleSave}
            onCancel={clearProgress}
            selectedRows={selectedRows}
            onSelectionChange={setSelectedRows}
            isDarkMode={isDarkMode}
          />
        </div>
      </section>
    </div>
  );
};

export default Developers;
